import React from 'react'
import cn from 'classnames'
import { Form, Formik } from 'formik'
import { useRouter } from 'next/router'

import { Button } from '@components/Buttons'
import { SelectButton } from '@components/SelectButton/SelectButton'
import { InputField } from '@components/forms/InputField'
import { SelectButtonField } from '@components/forms/SelectButtonField'
import { MultiSelectField } from '@components/forms/MultiSelectField'
import { SortField } from '@components/forms/SortField'
import { BookEnum } from '@interfaces/BookEnum'
import { CharacterOrderEnum } from '@interfaces/CharacterOrderEnum'
import { RecommendTypeSearchEnum } from '@interfaces/RecommendTypeSearchEnum'
import { AdvanceIcon } from '@components/icons/AdvanceIcon'
import { SearchIcon } from '@components/icons/SearchIcon'
import { useTheme } from '@hooks/contexts/ThemeContext/ThemeContext'
import { OptionFormProps } from './interface'

const bookTypeOptions = [
  { value: BookEnum.NOVEL, label: 'นิยาย' },
  { value: BookEnum.MANGA, label: 'การ์ตูน' },
]

const sortOptions = [
  {
    value: CharacterOrderEnum.LAST_ADDED,
    title: 'วันที่สร้าง',
    label: 'ใหม่ - เก่า',
  },
  {
    value: CharacterOrderEnum.LAST_UPDATED,
    title: 'วันอัปเดต',
    label: 'ใหม่ - เก่า',
  },
  {
    value: CharacterOrderEnum.MOST_COINS,
    title: 'ยอดสนับสนุน',
    label: 'มาก - น้อย',
  },
]

const chapterTypeOptions = [
  // { value: RecommendTypeSearchEnum.WHOLE, text: 'ตอนเดียว' },
  // { value: RecommendTypeSearchEnum.CHAPTER, text: 'มีมากกว่า 1 ตอน' },
  { value: RecommendTypeSearchEnum.ENDED, text: 'จบแล้ว' },
  { value: RecommendTypeSearchEnum.NOT_END, text: 'ยังไม่จบ' },
]

export default function OptionForm({
  categoryOptions,
  initialValues,
  showOption,
  characterRoleOptions,
  handleSearch,
  toggleOption,
}: OptionFormProps) {
  const router = useRouter()
  const { isNightOrDarkTheme } = useTheme()
  return (
    <Formik
      initialValues={initialValues}
      onSubmit={handleSearch}
      enableReinitialize
    >
      {({ values, setFieldValue, resetForm, isSubmitting }) => (
        <Form>
          <div className='flex mb-[20px] justify-between mobile:flex-col desktop-tablet:space-x-[20px]'>
            <InputField
              name='searchText'
              className='desktop:w-[970px] tablet:flex-1 mobile:w-full mobile:mb-[20px]'
              placeholder='ค้นหาด้วยชื่อเรื่อง, นักเขียน, นามปากกา หรืออื่นๆ'
              inputClassName='w-full bg-gray-4 h-[40px] leading-6 pl-[50px] border-gray-4 text-[14px] text-secondary placeholder:text-secondary-1 font-light rounded-[20px] p-[8px]'
              leftIcon={
                <SearchIcon className='text-primary' width='20' height='20' />
              }
            />
            <Button
              className={cn('border tablet:w-[162px]', {
                'text-white dark:text-white-2 bg-secondary dark:bg-purple dark:border-purple':
                  !showOption,
                'text-secondary dark:text-dark-10 border-secondary  dark:border-dark-10':
                  showOption,
              })}
              icon={
                <AdvanceIcon
                  className={cn('w-[22px] h-[22px]', {
                    'text-white dark:text-white-2': !showOption,
                    'text-secondary dark:text-dark-10': showOption,
                  })}
                />
              }
              type='button'
              onClick={toggleOption}
            >
              <span className='text-[14px] font-medium'>การค้นหาขั้นสูง</span>
            </Button>
          </div>
          <div className='flex-1 border-b-[1px] border-gray-3 dark:border-purple mb-[19.5px] pb-[19.5px] topic-base'>
            <SelectButton
              options={characterRoleOptions}
              value={values.characterRoleId}
              modeBoxBorderOptionClassName='border-none'
              onChange={value => {
                setFieldValue('characterRoleId', value)
                router.replace(
                  {
                    pathname: router.pathname,
                    query: { ...router.query, roleId: value },
                  },
                  undefined,
                  { scroll: false }
                )
              }}
            />
            {showOption && (
              <>
                <div className='grid grid-cols-2 mt-[30px] mb-[20px] mobile:grid-cols-1  gap-[20px] flex-1'>
                  <MultiSelectField
                    name='bookTypes'
                    label='ลักษณะงานเขียน'
                    placeholder='ลักษณะงานเขียน'
                    options={bookTypeOptions}
                  />
                  <MultiSelectField
                    name='categoryIds'
                    label='หมวดหมู่'
                    placeholder='หมวดหมู่'
                    options={categoryOptions}
                  />
                </div>
                <div className='grid grid-cols-3 gap-x-[20px] tablet:grid-cols-2 mobile:grid-cols-1'>
                  <SortField
                    name='characterOrder'
                    label='เรียงตาม'
                    className='w-[400px] mb-[20px] tablet-mobile:w-full'
                    options={sortOptions}
                  />
                  <div className='flex flex-1 px-[20px] mobile:overflow-x-auto mobile:mb-[20px] tablet-mobile:px-[0px] tablet:col-start-1 '>
                    <SelectButtonField
                      name='chapterType'
                      options={chapterTypeOptions}
                      mode='line'
                      label='ประเภท'
                      className={cn('!bg-white', {
                        '!bg-transparent': isNightOrDarkTheme,
                      })}
                    />
                  </div>
                  <div className='flex justify-end desktop:mt-[20px] mobile:justify-end tablet:items-end mobile:mb-[20px]'>
                    <Button
                      type='button'
                      className='text-secondary dark:text-dark-10 underline'
                      onClick={() => {
                        const newValues = {
                          searchText: '',
                          characterOrder: CharacterOrderEnum.LAST_ADDED,
                          categoryIds: [],
                          characterRoleId: 0,
                          chapterType: null,
                          bookTypes: [],
                        }
                        // resetForm({ values: newValues })
                        handleSearch(newValues)
                      }}
                    >
                      <span className='text-[14px] font-medium'>ล้างค่า</span>
                    </Button>
                    <Button
                      type='submit'
                      disabled={isSubmitting}
                      className='bg-primary dark:bg-purple text-white dark:text-white-2'
                    >
                      <span className='text-[14px]'>ค้นหา</span>
                    </Button>
                  </div>
                </div>
              </>
            )}
          </div>
        </Form>
      )}
    </Formik>
  )
}
